import { colorStrings } from "@iewcasia/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Checkbox, { CheckboxPresentationProps, StyleProps as CheckboxStyleProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface Props {
    id?: string;
    label: string;
    facetOptions: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
}

export interface SearchFilteringSelectorStyles {
    wrapper?: InjectableCss;
    facetLabelWrapper?: InjectableCss;
    facetLabel?: TypographyPresentationProps;
    searchTextWrapper?: InjectableCss;
    searchTextField?: TextFieldPresentationProps;
    checkBoxWrapper?: InjectableCss;
    checkBoxOption?: InjectableCss;
    checkBoxGroup?: CheckboxGroupProps;
    checkBoxLabel?: TypographyPresentationProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxLabelCounter?: TypographyPresentationProps;
}

export const searchFilteringSelectorStyles: SearchFilteringSelectorStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonGlobalGray04)};
            display: flex;
            flex-direction: column;
            text-wrap: nowrap;
        `,
    },
    facetLabelWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalGray01)};
            padding: 8px 24px;
            min-width: 215px;
        `,
    },
    facetLabel: {
        color: colorStrings.commonGlobalWhite,
        weight: 700,
    },
    searchTextField: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                background: ${getColor(colorStrings.commonGlobalWhite)};
            `,
        },
    },
    checkBoxWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            display: inline-flex;
            flex-direction: column;
            gap: 16px;
            height: 100%;
            height: 250px;
            overflow-x: hidden;
            overflow-y: auto;
            padding: 16px 24px;
            white-space: break-spaces;
            width: 100%;
        `,
    },
    checkBoxGroup: {
        css: css`
            width: 100%;

            &&& > ${TypographyStyle} {
                margin: 0;
            }
        `,
    },
    checkBox: {
        mergeCss: true,
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            width: 100%;

            &&& {
                span[role="checkbox"] {
                    margin: 2px 0 0;
                    min-width: 20px;
                }

                label {
                    margin-left: 8px;
                    width: 100%;
                }
            }
        `,
    },
    checkBoxLabel: {
        lineHeight: 1.3333,
        size: "12px",
        css: css`
            display: inline-flex;
            gap: 8px;
            justify-content: space-between;
            width: 100%;
        `,
    },
    checkBoxLabelCounter: {
        css: css`
            white-space: nowrap;
        `,
    },
};

const styles = searchFilteringSelectorStyles;

const SearchFilteringSelector: FC<Props> = ({ id, label, facetOptions, onChangeFacet }) => {
    const [searchText, setSearchText] = useState("");

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
        setSearchText(event.currentTarget.value);
    };

    const iconProps = searchText.length > 0 ? { src: X } : { src: Search };
    styles.searchTextField = {
        ...styles.searchTextField,
        iconProps,
    };

    const iconClickableProps: ClickablePresentationProps =
        searchText.length > 0
            ? {}
            : {
                  css: css`
                      pointer-events: none;
                  `,
              };

    const resetSearch = () => {
        setSearchText("");
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.facetLabelWrapper}>
                <Typography {...styles.facetLabel}>{label}</Typography>
            </StyledWrapper>

            <StyledWrapper {...styles.checkBoxWrapper}>
                {facetOptions.length >= 10 && (
                    <StyledWrapper {...styles.searchTextWrapper}>
                        <TextField
                            {...styles.searchTextField}
                            id={`facetCheckBoxSearch_${id}`}
                            data-test-selector="searchFilteringSearch"
                            iconClickableProps={{
                                ...iconClickableProps,
                                "aria-roledescription": `Search`,
                                onClick: resetSearch,
                            }}
                            value={searchText}
                            onChange={event => setSearchText(event.currentTarget.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={translate("Search")}
                            type="text"
                        />
                    </StyledWrapper>
                )}

                {facetOptions
                    .filter(facet => facet.name?.toLowerCase().indexOf(searchText.toLowerCase()) > -1)
                    .map(facet => {
                        const checkBox = {
                            ...styles.checkBox,
                            typographyProps: {
                                ...styles.checkBox?.typographyProps,
                                weight: facet.selected ? 700 : 400,
                            },
                        };

                        return (
                            <StyledWrapper
                                key={`facetSelect_${facet.id}`}
                                {...styles.checkBoxOption}
                                data-test-selector="facetCheckBoxOption"
                            >
                                <CheckboxGroup key={facet.id} {...styles.checkBoxGroup} label={null} error={null}>
                                    <Checkbox
                                        {...checkBox}
                                        checked={facet.selected}
                                        onChange={() => onChangeFacet(facet)}
                                        data-test-selector={`facetFilter${facet.id}`}
                                    >
                                        <Typography {...styles.checkBoxLabel}>
                                            {facet.name}
                                            {facet.count !== -1 && (
                                                <Typography {...styles.checkBoxLabelCounter}>
                                                    ({facet.count})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Checkbox>
                                </CheckboxGroup>
                            </StyledWrapper>
                        );
                    })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default SearchFilteringSelector;
